import { useEffect, useRef, useState, type ReactNode } from "react";
import { useAuth, type AuthUser } from "@/hooks/useAuth";

interface GsiCredentialResponse {
  credential: string;
  select_by: string;
}

interface GoogleAccountsId {
  initialize: (config: {
    client_id: string;
    callback: (resp: GsiCredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
    ux_mode?: "popup" | "redirect";
  }) => void;
  renderButton: (
    parent: HTMLElement,
    options: {
      type?: "standard" | "icon";
      theme?: "outline" | "filled_blue" | "filled_black";
      size?: "large" | "medium" | "small";
      text?: "signin_with" | "signup_with" | "continue_with" | "signin";
      shape?: "rectangular" | "pill" | "circle" | "square";
      logo_alignment?: "left" | "center";
      width?: string | number;
    },
  ) => void;
  disableAutoSelect: () => void;
}

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: GoogleAccountsId;
      };
    };
  }
}

interface AuthConfig {
  clientId: string;
  allowedDomains: string[];
}

interface LoginGateProps {
  children: (user: AuthUser, signOut: () => void) => ReactNode;
}

export function LoginGate({ children }: LoginGateProps) {
  const { user, loading, signIn, signOut } = useAuth();
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [gsiReady, setGsiReady] = useState(false);
  const buttonRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/auth/config")
      .then((r) => r.json())
      .then((data: AuthConfig) => {
        if (cancelled) return;
        if (!data.clientId) {
          setConfigError(
            "Google OAuth Client ID가 서버에 설정되지 않았습니다. GCP에서 Web OAuth Client를 만들고 GOOGLE_OAUTH_CLIENT_ID를 설정하세요.",
          );
          return;
        }
        setConfig(data);
      })
      .catch((e) => {
        if (!cancelled) setConfigError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (user) return;
    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      if (window.google?.accounts?.id) {
        setGsiReady(true);
      } else {
        setTimeout(check, 100);
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!config || !gsiReady || user) return;
    const google = window.google?.accounts?.id;
    if (!google || !buttonRef.current) return;
    google.initialize({
      client_id: config.clientId,
      callback: async (resp) => {
        setVerifyError(null);
        const result = await signIn(resp.credential);
        if (!result.ok) {
          setVerifyError(
            result.error === "domain_not_allowed"
              ? "허용되지 않은 도메인입니다. brandazine.kr 또는 brandazine.com 계정으로 로그인해주세요."
              : result.error || "로그인에 실패했습니다",
          );
        }
      },
      auto_select: false,
      cancel_on_tap_outside: true,
      ux_mode: "popup",
    });
    buttonRef.current.innerHTML = "";
    google.renderButton(buttonRef.current, {
      type: "standard",
      theme: "filled_blue",
      size: "large",
      text: "signin_with",
      shape: "pill",
      logo_alignment: "left",
    });
  }, [config, gsiReady, user, signIn]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (user) {
    return <>{children(user, signOut)}</>;
  }

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 px-6">
      <div className="w-full max-w-md bg-background rounded-2xl shadow-xl p-8 border border-border">
        <div className="text-center mb-8">
          <div className="text-4xl mb-3">🏢</div>
          <h1 className="text-2xl font-semibold mb-2">Brandazine 회의실</h1>
          <p className="text-sm text-muted-foreground">
            계속하려면 brandazine 계정으로 로그인하세요
          </p>
        </div>

        {configError && (
          <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {configError}
          </div>
        )}

        {verifyError && (
          <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {verifyError}
          </div>
        )}

        <div className="flex justify-center">
          <div ref={buttonRef} />
        </div>

        {!config && !configError && (
          <div className="text-center text-xs text-muted-foreground mt-4">
            설정 로드 중...
          </div>
        )}
        {config && !gsiReady && (
          <div className="text-center text-xs text-muted-foreground mt-4">
            Google 로그인 로드 중...
          </div>
        )}
      </div>
    </div>
  );
}
